import { useEffect } from 'react'
import { useStore } from '@tanstack/react-store'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getFieldError, type TareaForm as TareaFormType } from '@/components/tareas/forms'
import { useEtiquetasCliente } from '@/core/hooks/useEtiquetasCliente'
import { useContarClientesDeGrupo } from '@/core/hooks/useTareasGrupo'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { CONDICIONES_IVA, type CondicionIVA } from '@/workspaces/contable/types/faceta'
import {
  TIPOS_PERSONA_GRUPO,
  type TipoGrupoTarea,
  type TipoPersonaGrupo,
} from '@/core/services/tareasGrupoService'
import { Users } from 'lucide-react'

interface Props {
  form: TareaFormType
}

const LABEL_TIPO: Record<TipoGrupoTarea, string> = {
  etiqueta: 'Etiqueta',
  condicion_iva: 'Condición frente al IVA',
  impuesto: 'Impuesto activo',
  tipo_persona: 'Tipo de cliente',
}

const LABEL_VALOR: Record<TipoGrupoTarea, string> = {
  etiqueta: 'Etiqueta',
  condicion_iva: 'Condición',
  impuesto: 'Impuesto',
  tipo_persona: 'Tipo',
}

/**
 * Sección "Aplicar a un grupo de clientes" del formulario de tarea.
 *
 * Alternativa a un cliente único: crea una tarea por cada cliente activo de una etiqueta, una
 * condición frente al IVA, un impuesto activo o un tipo de cliente. Vale para una tarea suelta y
 * para una repetición. En una repetición el grupo se vuelve a resolver en cada ocurrencia. Los
 * clientes sin servicio mensual activo quedan afuera del grupo, y la vista previa lo dice.
 */
export function GrupoClientesSection({ form }: Props) {
  const repetir = useStore(form.store, (s) => s.values.repetir)
  const clienteId = useStore(form.store, (s) => s.values.cliente_id)
  const grupoTipo = useStore(form.store, (s) => s.values.grupo_tipo)
  const grupoValor = useStore(form.store, (s) => s.values.grupo_valor)

  const { data: etiquetas = [] } = useEtiquetasCliente()
  const { catalogo: impuestos } = useCatalogoImpuestos()
  const {
    data: conteo,
    isFetching: contando,
    isError: errorAlContar,
  } = useContarClientesDeGrupo(grupoTipo, grupoValor, repetir)

  // Con un cliente elegido la sección no se usa: el grupo se limpia para que el schema no
  // rechace el envío por un campo que el usuario no ve.
  useEffect(() => {
    if (clienteId && grupoTipo != null) {
      form.setFieldValue('grupo_tipo', null)
      form.setFieldValue('grupo_valor', null)
    }
  }, [clienteId, grupoTipo, form])

  const activo = grupoTipo != null
  const clientes = `${conteo ?? 0} cliente${conteo === 1 ? '' : 's'}`
  const sinEsporadicos = 'No incluye a los clientes sin honorario mensual.'
  const vistaPrevia = contando
    ? 'Contando clientes…'
    : errorAlContar
      ? 'No se pudo contar los clientes del grupo.'
      : repetir
        ? `Se crea una tarea para ${clientes} en cada repetición. Los clientes que entren al grupo más adelante también la reciben. ${sinEsporadicos}`
        : `Se va a crear una tarea para ${clientes}. ${sinEsporadicos}`

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <label className="flex cursor-pointer items-center gap-2">
        <Checkbox
          checked={activo}
          disabled={!!clienteId}
          onCheckedChange={(checked) => {
            form.setFieldValue('grupo_tipo', checked === true ? 'etiqueta' : null)
            form.setFieldValue('grupo_valor', null)
          }}
        />
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Aplicar a un grupo de clientes</span>
      </label>

      {clienteId && (
        <p className="text-xs text-muted-foreground">
          Esta tarea ya tiene un cliente. Quitalo si querés aplicarla a un grupo.
        </p>
      )}

      {activo && !clienteId && (
        <div className="space-y-3 pt-1">
          <form.Field name="grupo_tipo">
            {(field) => (
              <div className="space-y-2">
                <Label>Grupo</Label>
                <Select
                  value={field.state.value ?? 'etiqueta'}
                  onValueChange={(value) => {
                    field.handleChange(value as TipoGrupoTarea)
                    form.setFieldValue('grupo_valor', null)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LABEL_TIPO) as TipoGrupoTarea[]).map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>
                        {LABEL_TIPO[tipo]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="grupo_valor">
            {(field) => (
              <div className="space-y-2">
                <Label>{LABEL_VALOR[grupoTipo ?? 'etiqueta']}</Label>
                <Select
                  value={field.state.value ?? '_none'}
                  onValueChange={(value) => field.handleChange(value === '_none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Elegí un valor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Elegí un valor</SelectItem>
                    {grupoTipo === 'etiqueta' &&
                      etiquetas.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.nombre}
                        </SelectItem>
                      ))}
                    {grupoTipo === 'condicion_iva' &&
                      (Object.keys(CONDICIONES_IVA) as CondicionIVA[]).map((c) => (
                        <SelectItem key={c} value={c}>
                          {CONDICIONES_IVA[c]}
                        </SelectItem>
                      ))}
                    {grupoTipo === 'impuesto' &&
                      impuestos.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.nombre}
                        </SelectItem>
                      ))}
                    {grupoTipo === 'tipo_persona' &&
                      (Object.keys(TIPOS_PERSONA_GRUPO) as TipoPersonaGrupo[]).map((t) => (
                        <SelectItem key={t} value={t}>
                          {TIPOS_PERSONA_GRUPO[t]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>
                )}
              </div>
            )}
          </form.Field>

          {grupoValor && (
            <p className="rounded bg-muted px-3 py-2 text-sm text-muted-foreground">{vistaPrevia}</p>
          )}
        </div>
      )}
    </div>
  )
}
